package com.schoolconnect.app.feature.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.brand_header_proper
import ischoolconnect.sharedui.generated.resources.hero_scene_without_header
import ischoolconnect.sharedui.generated.resources.ic_clock_small
import ischoolconnect.sharedui.generated.resources.icon_login_lock
import ischoolconnect.sharedui.generated.resources.icon_user_input
import ischoolconnect.sharedui.generated.resources.trust_footer_bar
import com.schoolconnect.app.util.isValidOtp
import com.schoolconnect.app.util.BuildConfig
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.vectorResource
import androidx.compose.ui.graphics.vector.rememberVectorPainter

private val DeepNavy = Color(0xFF071463)
private val PrimaryBlue = Color(0xFF0B63F6)
private val BlueStart = Color(0xFF087BFF)
private val BlueEnd = Color(0xFF0052E6)
private val MutedText = Color(0xFF53689C)
private val Border = Color(0xFFCED8E7)
private val CardBorder = Color(0xFFEAF0FA)
private val BackgroundTop = Color(0xFFF7FBFF)
private val BackgroundBottom = Color(0xFFEAF6FF)

@Composable
fun ScreenBackground(content: @Composable () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(
                Brush.linearGradient(
                    colors = listOf(BackgroundTop, BackgroundBottom)
                )
            )
    ) {
        content()
    }
}

@Composable
fun BrandHeader(
    modifier: Modifier = Modifier,
    showLarge: Boolean = true,
    height: androidx.compose.ui.unit.Dp = 200.dp
) {
    Row(
        modifier = modifier.widthIn(max = if (showLarge) 920.dp else 560.dp).height(height),
        verticalAlignment = Alignment.CenterVertically
    ) {
        androidx.compose.foundation.Image(
            painter = painterResource(Res.drawable.brand_header_proper),
            contentDescription = null,
            modifier = Modifier.fillMaxWidth().fillMaxHeight(),
            contentScale = ContentScale.Fit,
            alignment = Alignment.CenterStart
        )
        Spacer(Modifier.weight(1f))
    }
}

@Composable
fun HeroIllustration(
    modifier: Modifier = Modifier,
    contentScale: ContentScale = ContentScale.Fit
) {
    androidx.compose.foundation.Image(
        painter = painterResource(Res.drawable.hero_scene_without_header),
        contentDescription = null,
        modifier = modifier.fillMaxWidth(),
        contentScale = contentScale
    )
}

@Composable
fun MarketingHeadline(
    text: String,
    modifier: Modifier = Modifier
) {
    Text(
        text = text,
        modifier = modifier,
        style = MaterialTheme.typography.titleLarge,
        fontWeight = FontWeight.Bold,
        color = DeepNavy
    )
}

@Composable
fun TrustFooterBar(
    modifier: Modifier = Modifier
) {
    androidx.compose.foundation.Image(
        painter = painterResource(Res.drawable.trust_footer_bar),
        contentDescription = null,
        modifier = modifier.fillMaxWidth(),
        contentScale = ContentScale.FillWidth
    )
}

@Composable
fun LoginCard(
    modifier: Modifier = Modifier,
    state: AuthUiState,
    compact: Boolean,
    landscape: Boolean,
    dense: Boolean = false,
    largeDesktop: Boolean = false,
    onEvent: (AuthUiEvent) -> Unit
) {
    Card(
        modifier = modifier
            .widthIn(max = if (largeDesktop) 560.dp else if (landscape) 590.dp else 560.dp)
            .then(if (largeDesktop) Modifier.heightIn(min = 820.dp) else Modifier),
        shape = RoundedCornerShape(if (landscape) 28.dp else 28.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        border = androidx.compose.foundation.BorderStroke(1.dp, CardBorder),
        elevation = CardDefaults.cardElevation(defaultElevation = 16.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(
                    when {
                        dense -> 28.dp
                        compact -> 32.dp
                        landscape -> 40.dp
                        else -> 48.dp
                    }
            ),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(
                when {
                    dense -> 16.dp
                    compact -> 28.dp
                    landscape -> 24.dp
                    else -> 28.dp
                }
            )
        ) {
            Box(
                modifier = Modifier
                    .width(
                        when {
                            dense -> 76.dp
                            compact -> 88.dp
                            landscape -> 104.dp
                            else -> 104.dp
                        }
                    )
                    .height(
                        when {
                            dense -> 76.dp
                            compact -> 88.dp
                            landscape -> 104.dp
                            else -> 104.dp
                        }
                    ),
                contentAlignment = Alignment.Center
            ) {
                androidx.compose.foundation.Image(
                    painter = painterResource(Res.drawable.icon_login_lock),
                    contentDescription = null,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Fit
                )
            }

            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(if (dense) 8.dp else 12.dp)
            ) {
                Text(
                    text = "Welcome Back!",
                    style = when {
                        dense -> MaterialTheme.typography.headlineSmall
                        compact -> MaterialTheme.typography.headlineSmall
                        landscape -> MaterialTheme.typography.headlineMedium
                        else -> MaterialTheme.typography.headlineMedium
                    },
                    fontWeight = FontWeight.Bold,
                    color = DeepNavy
                )
                Text(
                    text = "Sign in with your Email",
                    style = MaterialTheme.typography.titleMedium,
                    color = MutedText
                )
            }

            Column(verticalArrangement = Arrangement.spacedBy(if (dense) 10.dp else 14.dp)) {
                Text(
                    text = "Login ID (Email)",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = DeepNavy
                )
                LoginIdInput(
                    value = state.loginId,
                    onValueChange = { onEvent(AuthUiEvent.LoginIdChanged(it)) },
                    onGetOtp = { onEvent(AuthUiEvent.SendOtpClicked) },
                    enabled = !state.isLoading,
                    compact = compact
                )
            }

            Column(verticalArrangement = Arrangement.spacedBy(if (dense) 10.dp else 16.dp)) {
                Text(
                    text = "OTP Verification",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = DeepNavy
                )
                Text(
                    text = if (state.isOtpSent) {
                        "Enter the OTP you received to continue."
                    } else {
                        "Request OTP first to unlock verification."
                    },
                    style = MaterialTheme.typography.bodyMedium,
                    color = MutedText,
                    fontWeight = FontWeight.Medium
                )
                OtpInputBoxes(
                    value = state.otp,
                    onValueChange = { onEvent(AuthUiEvent.OtpChanged(it)) },
                    onDone = { onEvent(AuthUiEvent.VerifyOtpClicked) },
                    enabled = state.isOtpSent && !state.isLoading && !state.isOtpVerified,
                    compact = compact,
                    largeDesktop = largeDesktop
                )
                OtpResendHintRow(
                    isOtpSent = state.isOtpSent,
                    countdownSeconds = state.otpCountdownSeconds,
                    enabled = !state.isLoading,
                    onResend = { onEvent(AuthUiEvent.ResendOtpClicked) }
                )
            }

            Spacer(modifier = Modifier.height(if (dense || compact) 4.dp else 6.dp))

            PrimaryGradientButton(
                text = if (state.isLoading) "Working..." else "Continue",
                enabled = state.isOtpSent && !state.isOtpVerified && !state.isLoading && isValidOtp(state.otp),
                modifier = Modifier.fillMaxWidth(),
                height = when {
                    dense || compact -> 56.dp
                    landscape -> 64.dp
                    else -> 68.dp
                },
                onClick = { onEvent(AuthUiEvent.VerifyOtpClicked) }
            )

            Spacer(modifier = Modifier.height(if (dense || compact) 2.dp else 4.dp))

            state.errorMessage.takeIf { it.isNotBlank() }?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color(0xFFD92D20),
                    fontWeight = FontWeight.Medium
                )
            }
            state.infoMessage.takeIf { it.isNotBlank() }?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color(0xFF05A96F),
                    fontWeight = FontWeight.Medium
                )
            }

            HelpRow()
        }
    }
}

@Composable
fun OtpResendHintRow(
    isOtpSent: Boolean,
    countdownSeconds: Int,
    enabled: Boolean,
    onResend: () -> Unit
) {
    if (!isOtpSent) return

    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            androidx.compose.foundation.Image(
                painter = rememberVectorPainter(vectorResource(Res.drawable.ic_clock_small)),
                contentDescription = null,
                modifier = Modifier.width(18.dp).height(18.dp)
            )

            if (countdownSeconds > 0) {
                Text(
                    text = "OTP sent. You can continue with this code.",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MutedText,
                    fontWeight = FontWeight.Medium
                )
                OutlinedButton(
                    onClick = onResend,
                    enabled = false,
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(
                        horizontal = 12.dp,
                        vertical = 0.dp
                    )
                ) {
                    Text("Resend in ${formatCountdown(countdownSeconds)}")
                }
            } else {
                Text(
                    text = "Didn't get it?",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MutedText,
                    fontWeight = FontWeight.Medium
                )
                OutlinedButton(
                    onClick = onResend,
                    enabled = enabled,
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(
                        horizontal = 12.dp,
                        vertical = 0.dp
                    )
                ) {
                    Text("Resend OTP")
                }
            }
        }

        Text(
            text = "Resending sends another code; this code remains usable until it expires.",
            style = MaterialTheme.typography.bodySmall,
            color = MutedText
        )
    }
}

@Composable
fun LoginIdInput(
    value: String,
    onValueChange: (String) -> Unit,
    onGetOtp: () -> Unit,
    enabled: Boolean,
    compact: Boolean
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        // Keep the email field and Get OTP action together on desktop cards.
        // The card's inner width can be below 500dp after padding while still
        // having enough room for the reference two-column form row.
        val stacked = compact || maxWidth < 420.dp
        if (stacked) {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                InputField(
                    value = value,
                    onValueChange = onValueChange,
                    enabled = enabled,
                    modifier = Modifier.fillMaxWidth()
                )
                PrimaryGradientButton(
                    text = "Get OTP",
                    enabled = enabled,
                    onClick = onGetOtp,
                    modifier = Modifier.fillMaxWidth(),
                    height = 56.dp
                )
            }
        } else {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                InputField(
                    value = value,
                    onValueChange = onValueChange,
                    enabled = enabled,
                    modifier = Modifier.weight(1f)
                )
                PrimaryGradientButton(
                    text = "Get OTP",
                    enabled = enabled,
                    onClick = onGetOtp,
                    modifier = Modifier.width(112.dp),
                    height = 56.dp
                )
            }
        }
    }
}

@Composable
private fun InputField(
    value: String,
    onValueChange: (String) -> Unit,
    enabled: Boolean,
    modifier: Modifier = Modifier
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier,
        enabled = enabled,
        singleLine = true,
        placeholder = { Text("Enter your email") },
        leadingIcon = {
            Icon(
                painter = painterResource(Res.drawable.icon_user_input),
                contentDescription = null,
                tint = Color.Unspecified
            )
        },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = PrimaryBlue,
            unfocusedBorderColor = Border,
            focusedContainerColor = Color.White,
            unfocusedContainerColor = Color.White,
            cursorColor = PrimaryBlue,
            focusedTextColor = DeepNavy,
            unfocusedTextColor = DeepNavy,
            focusedPlaceholderColor = MutedText,
            unfocusedPlaceholderColor = MutedText,
            focusedLeadingIconColor = MutedText,
            unfocusedLeadingIconColor = MutedText
        )
    )
}

@Composable
fun OtpInputBoxes(
    value: String,
    onValueChange: (String) -> Unit,
    onDone: () -> Unit,
    enabled: Boolean,
    compact: Boolean,
    largeDesktop: Boolean = false,
    length: Int = 6
) {
    val normalized = value.filter(Char::isDigit).take(length)
    val digits = (0 until length).map { index -> normalized.getOrNull(index)?.toString().orEmpty() }
    val focusRequester = remember { FocusRequester() }
    val activeIndex = if (normalized.isEmpty()) 0 else normalized.length.coerceAtMost(length - 1)

    LaunchedEffect(enabled) {
        if (enabled) {
            focusRequester.requestFocus()
        }
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .alpha(if (enabled) 1f else 0.52f)
            .clickable(
                enabled = enabled,
                indication = null,
                interactionSource = remember { MutableInteractionSource() }
            ) {
                focusRequester.requestFocus()
            }
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxWidth()
                .widthIn(max = if (compact) androidx.compose.ui.unit.Dp.Infinity else 500.dp)
        ) {
            val gap = if (compact) 12.dp else 14.dp
            val desktopCellWidth = ((maxWidth - (gap * 5)) / 6).coerceAtLeast(1.dp)

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(gap)
            ) {
                digits.forEachIndexed { index, digit ->
                    val active = enabled && index == activeIndex
                    Box(
                        modifier = Modifier
                            .then(
                                if (compact) Modifier.weight(1f)
                                else Modifier.width(desktopCellWidth)
                            )
                            .height(if (compact) 52.dp else 54.dp)
                        .border(
                            width = 1.5.dp,
                            color = if (active) PrimaryBlue else Border,
                            shape = RoundedCornerShape(10.dp)
                        )
                        .background(Color.White, RoundedCornerShape(10.dp)),
                    contentAlignment = Alignment.Center
                    ) {
                        Text(
                        text = digit,
                        style = MaterialTheme.typography.headlineSmall.copy(
                            fontWeight = FontWeight.SemiBold,
                            color = DeepNavy
                        )
                        )
                    }
                }
            }
        }

        BasicTextField(
            value = normalized,
            onValueChange = { newValue ->
                onValueChange(newValue.filter(Char::isDigit).take(length))
            },
            modifier = Modifier
                .focusRequester(focusRequester)
                .size(1.dp)
                .alpha(if (enabled) 0.01f else 0f),
            enabled = enabled,
            singleLine = true,
            textStyle = MaterialTheme.typography.headlineSmall.copy(
                fontWeight = FontWeight.SemiBold,
                color = Color.Transparent
            ),
            cursorBrush = SolidColor(Color.Transparent),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Number,
                imeAction = ImeAction.Done
            ),
            keyboardActions = KeyboardActions(
                onDone = {
                    onDone()
                }
            )
        )
    }
}

@Composable
fun PrimaryGradientButton(
    text: String,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    height: androidx.compose.ui.unit.Dp = 68.dp
) {
    Box(
        modifier = modifier
            .height(height)
            .alpha(if (enabled) 1f else 0.55f)
            .background(
                brush = Brush.horizontalGradient(listOf(BlueStart, BlueEnd)),
                shape = RoundedCornerShape(10.dp)
            )
            .clickable(enabled = enabled, onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = text,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.SemiBold,
            color = Color.White.copy(alpha = if (enabled) 1f else 0.65f)
        )
    }
}

@Composable
fun HelpRow() {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = "Need help? ",
            style = MaterialTheme.typography.bodyLarge,
            color = MutedText,
            fontWeight = FontWeight.Medium
        )
        Text(
            text = "Contact Support",
            style = MaterialTheme.typography.bodyLarge,
            color = PrimaryBlue,
            fontWeight = FontWeight.SemiBold
        )
    }
}

@Composable
fun FooterLinks(
    modifier: Modifier = Modifier,
    compact: Boolean
) {
    if (compact) {
        Column(
            modifier = modifier.padding(top = 4.dp, bottom = 0.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Text(
                text = "© 2026 iConnect.School. All rights reserved.",
                style = MaterialTheme.typography.bodySmall,
                color = MutedText
            )
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                Text("Privacy Policy", color = PrimaryBlue, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.SemiBold)
                Text("|", color = Border)
                Text("Terms of Use", color = PrimaryBlue, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.SemiBold)
                Text("|", color = Border)
                Text("Contact Us", color = PrimaryBlue, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.SemiBold)
            }
            Text(
                text = "Version ${BuildConfig.getVersion()}",
                style = MaterialTheme.typography.bodySmall,
                color = MutedText
            )
        }
    } else {
        Row(
            modifier = modifier.padding(top = 4.dp, bottom = 0.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "© 2026 iConnect.School. All rights reserved.",
                style = MaterialTheme.typography.bodyMedium,
                color = MutedText
            )
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.CenterVertically) {
                Text("Privacy Policy", color = PrimaryBlue, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
                Text("|", color = Border)
                Text("Terms of Use", color = PrimaryBlue, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
                Text("|", color = Border)
                Text("Contact Us", color = PrimaryBlue, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.SemiBold)
                Text("|", color = Border)
                Text("Version ${BuildConfig.getVersion()}", color = MutedText, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Medium)
            }
        }
    }
}
